import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, Button, Input, Select, Modal, Badge } from '../components/Common';
import NotificationChannels from '../components/NotificationChannels';
import LogStreaming from '../components/LogStreaming';

// What an inbound ticket close does to the finding. Only a close is governed:
// a ticket reopening always reopens the finding, whichever of these is set.
const CLOSE_POLICIES = [
  { value: 'close', label: 'Close the finding' },
  { value: 'retest', label: 'Move the finding to Retest (a tester confirms the fix)' },
  { value: 'ignore', label: 'Leave the finding alone (only a tester may close it)' },
];

const SYNC_DIRECTIONS = [
  { value: 'push', label: 'Push Only (findings -> tickets)' },
  { value: 'pull', label: 'Pull Only (tickets -> findings)' },
  { value: 'bidirectional', label: 'Bidirectional' },
];

const JIRA_ISSUE_TYPES = ['Bug', 'Task', 'Story', 'Security', 'Vulnerability', 'Sub-task'];

export default function IntegrationSettings() {
  const { clientId: routeClientId } = useParams();
  const { apiFetch, user, hasGroup } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(routeClientId || '');
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [showLogModal, setShowLogModal] = useState(false);
  const [syncLog, setSyncLog] = useState([]);
  const [logIntegrationId, setLogIntegrationId] = useState(null);

  const isMsp = user?.isMsp || user?.groups?.includes('Super Admins');
  const isClientAdmin = user?.groups?.includes('Client Admins');

  // Load clients list for MSP users
  useEffect(() => {
    if (isMsp) {
      apiFetch('/clients').then(r => r.json()).then(data => {
        setClients(data.filter(c => c.is_active !== false));
      });
    } else if (user?.clientId) {
      setSelectedClientId(user.clientId);
    }
  }, [apiFetch, isMsp, user]);

  // Load integrations when client selected
  const loadIntegrations = useCallback(async () => {
    // 'msp' is a scope for notification channels, not a client — asking the
    // ticketing endpoint for it sends a non-UUID and logs a database error.
    if (!selectedClientId || selectedClientId === 'msp') { setIntegrations([]); return; }
    setLoading(true);
    try {
      const res = await apiFetch(`/integrations/${selectedClientId}`);
      if (res.ok) setIntegrations(await res.json());
      else setIntegrations([]);
    } catch { setIntegrations([]); }
    setLoading(false);
  }, [apiFetch, selectedClientId]);

  useEffect(() => { loadIntegrations(); }, [loadIntegrations]);

  const openAddModal = (type) => {
    setEditing(null);
    setTestResult(null);
    setForm({
      integrationType: type,
      instanceUrl: '',
      authEmail: '',
      apiCredentials: '',
      projectKey: '',
      issueType: 'Bug',
      tableName: 'incident',
      autoSyncEnabled: true,
      syncDirection: 'bidirectional',
      inboundClosePolicy: 'close',
    });
    setShowModal(true);
  };

  const openEditModal = (integration) => {
    setEditing(integration);
    setTestResult(null);
    setForm({
      integrationType: integration.integration_type,
      instanceUrl: integration.instance_url,
      authEmail: integration.auth_email || '',
      apiCredentials: '', // blank = keep existing
      projectKey: integration.project_key || '',
      issueType: integration.issue_type || 'Bug',
      tableName: integration.table_name || 'incident',
      autoSyncEnabled: integration.auto_sync_enabled,
      syncDirection: integration.sync_direction || 'push',
      inboundClosePolicy: integration.inbound_close_policy || 'close',
    });
    setShowModal(true);
  };

  const saveIntegration = async () => {
    setSaving(true);
    try {
      const body = {
        clientId: selectedClientId,
        integrationType: form.integrationType,
        instanceUrl: form.instanceUrl,
        authEmail: form.authEmail || null,
        apiCredentials: form.apiCredentials || undefined,
        projectKey: form.projectKey || null,
        issueType: form.issueType,
        tableName: form.tableName,
        autoSyncEnabled: form.autoSyncEnabled,
        syncDirection: form.syncDirection,
        inboundClosePolicy: form.inboundClosePolicy || 'close',
      };
      // Don't send empty credentials on update
      if (editing && !form.apiCredentials) delete body.apiCredentials;

      let res;
      if (editing) {
        res = await apiFetch(`/integrations/${editing.id}`, { method: 'PUT', body: JSON.stringify(body) });
      } else {
        res = await apiFetch('/integrations', { method: 'POST', body: JSON.stringify(body) });
      }
      if (res.ok) {
        setShowModal(false);
        loadIntegrations();
      } else {
        const data = await res.json();
        alert(data.error || 'Failed to save integration');
      }
    } catch (err) {
      alert('Failed to save: ' + err.message);
    }
    setSaving(false);
  };

  const deleteIntegration = async (id) => {
    if (!window.confirm('Deactivate this integration? Existing ticket links will be preserved.')) return;
    await apiFetch(`/integrations/${id}`, { method: 'DELETE' });
    loadIntegrations();
  };

  const testConnection = async (id) => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await apiFetch(`/integrations/${id}/test`, { method: 'POST' });
      const data = await res.json();
      setTestResult(data);
    } catch {
      setTestResult({ success: false, error: 'Connection test failed' });
    }
    setTesting(false);
  };

  const toggleAutoSync = async (integration) => {
    await apiFetch(`/integrations/${integration.id}`, {
      method: 'PUT',
      body: JSON.stringify({ autoSyncEnabled: !integration.auto_sync_enabled })
    });
    loadIntegrations();
  };

  const viewSyncLog = async (integrationId) => {
    setLogIntegrationId(integrationId);
    try {
      const res = await apiFetch(`/integrations/sync-log/${integrationId}`);
      if (res.ok) setSyncLog(await res.json());
      else setSyncLog([]);
    } catch { setSyncLog([]); }
    setShowLogModal(true);
  };

  const getWebhookUrl = (integration) => {
    const base = window.location.origin;
    return `${base}/api/integrations/webhook/${integration.id}`;
  };

  const clientName = isMsp
    ? clients.find(c => c.id === selectedClientId)?.name || ''
    : '';

  const hasJira = integrations.some(i => i.integration_type === 'jira' && i.is_active);
  const hasServiceNow = integrations.some(i => i.integration_type === 'servicenow' && i.is_active);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 4 }}>Integrations</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
            Configure Jira and ServiceNow integrations to sync findings with external ticketing systems.
          </p>
        </div>
      </div>

      {/* Client Selector (MSP only) */}
      {isMsp && (
        <Card style={{ marginBottom: 24 }}>
          <Select
            label="Select Client"
            options={[
              { value: '', label: 'Choose a client...' },
              { value: 'msp', label: 'MSP (our own notification channels)' },
              ...clients.map(c => ({ value: c.id, label: c.name })),
            ]}
            value={selectedClientId}
            onChange={e => { setSelectedClientId(e.target.value); navigate(`/integrations/${e.target.value}`, { replace: true }); }}
            style={{ marginBottom: 0, maxWidth: 400 }}
          />
        </Card>
      )}

      {!selectedClientId && (
        <Card style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-secondary)' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🔗</div>
          <p style={{ fontSize: 16 }}>Select a client to manage their integrations</p>
        </Card>
      )}

      {selectedClientId === 'msp' && (
        <>
          <NotificationChannels scope="msp" scopeLabel="your own team" />
          <LogStreaming scope="msp" scopeLabel="every client" />
        </>
      )}

      {selectedClientId && selectedClientId !== 'msp' && (
        <>
          {/* Add Integration Buttons */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
            {!hasJira && (
              <Button onClick={() => openAddModal('jira')}
                style={{ background: '#0052CC', color: '#fff', border: 'none' }}>
                + Add Jira Integration
              </Button>
            )}
            {!hasServiceNow && (
              <Button onClick={() => openAddModal('servicenow')}
                style={{ background: '#81B5A1', color: '#000', border: 'none' }}>
                + Add ServiceNow Integration
              </Button>
            )}
          </div>

          {/* Integration Cards */}
          {loading ? (
            <Card style={{ textAlign: 'center', padding: 40 }}>Loading...</Card>
          ) : integrations.length === 0 ? (
            <Card style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-secondary)' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>🔌</div>
              <p style={{ fontSize: 16, marginBottom: 8 }}>No integrations configured</p>
              <p style={{ fontSize: 13 }}>Add a Jira or ServiceNow integration to start syncing findings to external tickets.</p>
            </Card>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              {integrations.map(integ => (
                <Card key={integ.id} style={{
                  borderLeft: `4px solid ${integ.integration_type === 'jira' ? '#0052CC' : '#81B5A1'}`,
                  opacity: integ.is_active ? 1 : 0.5
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                        <span style={{ fontSize: 24 }}>{integ.integration_type === 'jira' ? '🔵' : '🟢'}</span>
                        <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>
                          {integ.integration_type === 'jira' ? 'Jira' : 'ServiceNow'}
                        </h3>
                        {integ.is_active ? (
                          <Badge>Active</Badge>
                        ) : (
                          <Badge severity="Cancelled">Inactive</Badge>
                        )}
                      </div>
                      <div style={{ fontSize: 13, color: 'var(--text-secondary)', wordBreak: 'break-all' }}>
                        {integ.instance_url}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <Button variant="secondary" style={{ padding: '4px 12px', fontSize: 12 }}
                        onClick={() => openEditModal(integ)}>Edit</Button>
                      {integ.is_active && (
                        <Button variant="danger" style={{ padding: '4px 12px', fontSize: 12 }}
                          onClick={() => deleteIntegration(integ.id)}>Disable</Button>
                      )}
                    </div>
                  </div>

                  {/* Config Details */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 13, marginBottom: 16 }}>
                    {integ.integration_type === 'jira' && (
                      <>
                        <div><strong>Project:</strong> {integ.project_key || '—'}</div>
                        <div><strong>Issue Type:</strong> {integ.issue_type || 'Bug'}</div>
                        <div><strong>Auth Email:</strong> {integ.auth_email || '—'}</div>
                      </>
                    )}
                    {integ.integration_type === 'servicenow' && (
                      <>
                        <div><strong>Table:</strong> {integ.table_name || 'incident'}</div>
                        <div><strong>Auth User:</strong> {integ.auth_email || '—'}</div>
                      </>
                    )}
                    <div><strong>Sync:</strong> {integ.sync_direction || 'push'}</div>
                    {(integ.sync_direction || 'push') !== 'push' && (integ.inbound_close_policy || 'close') !== 'close' && (
                      <div><strong>Ticket closed:</strong> {
                        (integ.inbound_close_policy === 'retest') ? 'finding goes to Retest' : 'finding unchanged'
                      }</div>
                    )}
                    <div><strong>Credentials:</strong> {integ.hasCredentials ? '✅ Set' : '❌ Missing'}</div>
                  </div>

                  {/* Auto-Sync Toggle */}
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '10px 14px', background: 'var(--bg-input)', borderRadius: 8, marginBottom: 12
                  }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600 }}>Auto-Sync</div>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                        Automatically update tickets when finding status changes
                      </div>
                    </div>
                    <button
                      onClick={() => toggleAutoSync(integ)}
                      style={{
                        width: 48, height: 26, borderRadius: 13, border: 'none', cursor: 'pointer',
                        background: integ.auto_sync_enabled ? '#22c55e' : '#64748b',
                        position: 'relative', transition: 'background 0.2s'
                      }}
                    >
                      <div style={{
                        width: 20, height: 20, borderRadius: '50%', background: '#fff',
                        position: 'absolute', top: 3,
                        left: integ.auto_sync_enabled ? 25 : 3,
                        transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                      }} />
                    </button>
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <Button variant="secondary" style={{ padding: '6px 14px', fontSize: 12 }}
                      onClick={() => testConnection(integ.id)} disabled={testing}>
                      {testing ? '⏳ Testing...' : '🔌 Test Connection'}
                    </Button>
                    <Button variant="secondary" style={{ padding: '6px 14px', fontSize: 12 }}
                      onClick={() => viewSyncLog(integ.id)}>
                      📜 Sync Log
                    </Button>
                    <Button variant="secondary" style={{ padding: '6px 14px', fontSize: 12 }}
                      onClick={() => {
                        navigator.clipboard.writeText(getWebhookUrl(integ));
                        alert('Webhook URL copied to clipboard!');
                      }}>
                      📋 Copy Webhook URL
                    </Button>
                  </div>

                  {/* Test Result inline */}
                  {testResult && (
                    <div style={{
                      marginTop: 12, padding: '10px 14px', borderRadius: 8, fontSize: 13,
                      background: testResult.success ? '#14532d' : '#7f1d1d',
                      color: testResult.success ? '#86efac' : '#fca5a5'
                    }}>
                      {testResult.success ? '✅ ' : '❌ '}{testResult.message || testResult.error || 'Unknown result'}
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}

          <NotificationChannels scope={selectedClientId} scopeLabel={clientName} />
          <LogStreaming scope={selectedClientId} scopeLabel={clientName} />
        </>
      )}

      {/* Add/Edit Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)}
        title={editing ? `Edit ${form.integrationType === 'jira' ? 'Jira' : 'ServiceNow'} Integration` : `Add ${form.integrationType === 'jira' ? 'Jira' : 'ServiceNow'} Integration`}
        width={600}>
        <Input label="Instance URL *"
          value={form.instanceUrl || ''}
          onChange={e => setForm(f => ({ ...f, instanceUrl: e.target.value }))}
          placeholder={form.integrationType === 'jira' ? 'https://yourcompany.atlassian.net' : 'https://yourinstance.service-now.com'} />

        {form.integrationType === 'jira' ? (
          <>
            <Input label="Email *" value={form.authEmail || ''} placeholder="user@company.com"
              onChange={e => setForm(f => ({ ...f, authEmail: e.target.value }))} />
            <Input label={editing ? 'API Token (leave blank to keep existing)' : 'API Token *'}
              type="password" value={form.apiCredentials || ''} placeholder="Jira API token"
              onChange={e => setForm(f => ({ ...f, apiCredentials: e.target.value }))} />
            <Input label="Project Key *" value={form.projectKey || ''} placeholder="e.g., SEC"
              onChange={e => setForm(f => ({ ...f, projectKey: e.target.value }))} />
            <Select label="Issue Type"
              options={JIRA_ISSUE_TYPES.map(t => ({ value: t, label: t }))}
              value={form.issueType || 'Bug'}
              onChange={e => setForm(f => ({ ...f, issueType: e.target.value }))} />
          </>
        ) : (
          <>
            <Input label="Username *" value={form.authEmail || ''} placeholder="admin"
              onChange={e => setForm(f => ({ ...f, authEmail: e.target.value }))} />
            <Input label={editing ? 'Password (leave blank to keep existing)' : 'Password *'}
              type="password" value={form.apiCredentials || ''} placeholder="ServiceNow password"
              onChange={e => setForm(f => ({ ...f, apiCredentials: e.target.value }))} />
            <Input label="Table Name" value={form.tableName || 'incident'} placeholder="incident"
              onChange={e => setForm(f => ({ ...f, tableName: e.target.value }))} />
          </>
        )}

        <Select label="Sync Direction" options={SYNC_DIRECTIONS}
          value={form.syncDirection || 'push'}
          onChange={e => setForm(f => ({ ...f, syncDirection: e.target.value }))} />

        {form.syncDirection !== 'push' && (
          <div style={{ marginBottom: 16 }}>
            <Select label="When a ticket is closed" options={CLOSE_POLICIES}
              value={form.inboundClosePolicy || 'close'}
              onChange={e => setForm(f => ({ ...f, inboundClosePolicy: e.target.value }))} />
            <div style={{ fontSize: 12, color: '#64748b', marginTop: -8 }}>
              Closing a ticket and closing a finding are separate decisions. Choose
              Retest or Leave alone where the client closes their own tickets but a
              tester owns the verdict. A ticket being reopened always reopens the
              finding, whichever option is set.
            </div>
          </div>
        )}

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
            <input type="checkbox" checked={form.autoSyncEnabled || false}
              onChange={e => setForm(f => ({ ...f, autoSyncEnabled: e.target.checked }))} />
            Enable Auto-Sync (automatically update tickets when finding status changes)
          </label>
        </div>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button onClick={saveIntegration} disabled={saving || !form.instanceUrl || (!editing && !form.apiCredentials)}>
            {saving ? 'Saving...' : 'Save Integration'}
          </Button>
        </div>
      </Modal>

      {/* Sync Log Modal */}
      <Modal isOpen={showLogModal} onClose={() => setShowLogModal(false)} title="Sync Log" width={800}>
        {syncLog.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '32px 20px', color: 'var(--text-secondary)' }}>
            No sync activity yet
          </div>
        ) : (
          <div style={{ maxHeight: 500, overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border)' }}>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600 }}>Time</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600 }}>Action</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600 }}>Direction</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600 }}>Status</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600 }}>Triggered By</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600 }}>Details</th>
                </tr>
              </thead>
              <tbody>
                {syncLog.map(log => (
                  <tr key={log.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                      {new Date(log.created_at).toLocaleString()}
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <Badge>{log.action.replace(/_/g, ' ')}</Badge>
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      {log.direction === 'outbound' ? '⬆️ Out' : '⬇️ In'}
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <span style={{
                        color: log.status === 'success' ? '#22c55e' : '#ef4444',
                        fontWeight: 600
                      }}>
                        {log.status === 'success' ? '✅' : '❌'} {log.status}
                      </span>
                    </td>
                    <td style={{ padding: '8px 12px' }}>{log.triggered_by_name || '—'}</td>
                    <td style={{ padding: '8px 12px', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {log.error_message || (log.details ? JSON.stringify(log.details) : '—')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <Button variant="secondary" onClick={() => setShowLogModal(false)}>Close</Button>
        </div>
      </Modal>
    </div>
  );
}
